import { PipelineRunStatus } from '../constants/pipelineRunStatus';
import { NEVER_RUN_STATUS_VALUE, PIPELINE_LIST_URL_PARAMS, SORTABLE_PIPELINE_FIELDS, SORT_DIRECTIONS } from '../constants/pipelineListNarrowing';

// pipelines-list-sort-and-narrow AC-16 — the Pipelines list's narrowing state <-> URL codec.
//
// ⚠ SPEC D3: THE URL IS THE SINGLE SOURCE OF TRUTH. This slice deliberately does NOT also write
// to the redux `filter: {}` on the pipelines slice, even though that is the house idiom
// (AppInstanceTable.js:69). Writing to both would give one view two sources of truth, which is how
// a list starts disagreeing with its own address bar. `filter: {}` is left untouched and still
// unused; if a later paging migration adopts it, THIS is where it would read its initial state.
//
// The URL was chosen because it is the only option that satisfies both of the epic's asks at once:
// narrowing survives the detail-page round trip (AC-15), AND a narrowed view is shareable and
// bookmarkable (AC-16b) — the epic's first headline success signal.

// The canonical empty state. Also the fallback for every unparseable input, which is what makes
// `parse` total: there is no input for which it throws or returns undefined.
export const EMPTY_PIPELINE_LIST_VIEW_STATE = Object.freeze({
	searchTerm: '',
	appInstances: [],
	statuses: [],
	sortField: null,
	sortDirection: null
});

const VALID_STATUS_VALUES = new Set([...Object.values(PipelineRunStatus).map(value => String(value)), NEVER_RUN_STATUS_VALUE]);

// AC-16 — serialise. OMITS every empty/default value rather than emitting `?q=&sort=`, so a
// pristine list has a CLEAN url and the shareable link contains only what the reader actually
// chose. It also makes the round-trip identity hold for the empty state.
export const serialisePipelineListViewState = (state = {}) => {
	const params = new URLSearchParams();
	const { searchTerm, appInstances, statuses, sortField, sortDirection } = { ...EMPTY_PIPELINE_LIST_VIEW_STATE, ...state };

	if (searchTerm) params.set(PIPELINE_LIST_URL_PARAMS.SEARCH, searchTerm);

	// Repeated params, never a joined string: App Instance names are free text and may contain a
	// comma (see constants/pipelineListNarrowing.js).
	(appInstances ?? []).forEach(value => params.append(PIPELINE_LIST_URL_PARAMS.APP_INSTANCE, value));
	(statuses ?? []).forEach(value => params.append(PIPELINE_LIST_URL_PARAMS.STATUS, String(value)));

	// A sort is only meaningful as a (field, direction) PAIR, so a half-specified sort is written as
	// nothing at all — the alternative is a URL that restores a field with no direction and leaves
	// the grid in a state the UI has no way to display.
	if (sortField && sortDirection) {
		params.set(PIPELINE_LIST_URL_PARAMS.SORT_FIELD, sortField);
		params.set(PIPELINE_LIST_URL_PARAMS.SORT_DIRECTION, sortDirection);
	}

	return params.toString();
};

// AC-16 — parse. TOTAL, AND SILENTLY FORGIVING BY DESIGN.
//
// A hand-edited or truncated URL must never white-screen the list: every unrecognised value is
// DROPPED, not thrown on and not passed through. Concretely — an unknown sort field is ignored
// (the grid would have no column for it), a direction that is not asc/desc is ignored, and a
// status that is not a shipped PipelineRunStatus member or the never-run sentinel is dropped from
// the selection while the VALID members of the same param are kept.
//
// Security note (NFR-Security): the only values that survive parsing are drawn from fixed
// allow-lists, except `searchTerm` and App Instance names, which are free text by nature. Those
// two reach React as text children and props only — never `dangerouslySetInnerHTML`, never a URL
// or style sink — so React's own escaping is what makes them safe, and this parser does not need
// to sanitise them. Cross-ref developer-portal-log-output-xss.
export const parsePipelineListViewState = search => {
	const params = new URLSearchParams(search ?? '');

	const searchTerm = params.get(PIPELINE_LIST_URL_PARAMS.SEARCH) ?? '';
	const appInstances = params.getAll(PIPELINE_LIST_URL_PARAMS.APP_INSTANCE).filter(value => value !== '');
	const statuses = params.getAll(PIPELINE_LIST_URL_PARAMS.STATUS).filter(value => VALID_STATUS_VALUES.has(value));

	const rawField = params.get(PIPELINE_LIST_URL_PARAMS.SORT_FIELD);
	const rawDirection = params.get(PIPELINE_LIST_URL_PARAMS.SORT_DIRECTION);
	const fieldIsValid = SORTABLE_PIPELINE_FIELDS.includes(rawField);
	const directionIsValid = SORT_DIRECTIONS.includes(rawDirection);
	// Both or neither, for the same reason serialise writes both or neither.
	const sortIsValid = fieldIsValid && directionIsValid;

	return {
		searchTerm,
		appInstances,
		statuses,
		sortField: sortIsValid ? rawField : null,
		sortDirection: sortIsValid ? rawDirection : null
	};
};

// The grid speaks `sortModel`; the URL speaks (field, direction). Two adapters, so neither side
// has to know the other's shape.
export const toGridSortModel = ({ sortField, sortDirection } = {}) => (sortField && sortDirection ? [{ field: sortField, sort: sortDirection }] : []);

export const fromGridSortModel = (sortModel = []) => {
	const [first] = sortModel ?? [];
	if (!first?.field || !first?.sort) return { sortField: null, sortDirection: null };
	return { sortField: first.field, sortDirection: first.sort };
};

// Review LOW-3 (2026-08-05) — the LIST's address, rebuilt from a remembered query string.
//
// Making the URL the list's state (D3) bought AC-15's back button for free, but it also created an
// inconsistency this slice had not noticed: the detail page's back ARROW calls navigate(-1) and so
// keeps the narrowing, while the breadcrumb LINK two elements away pointed at a bare `/pipelines`
// and threw it away. Two adjacent ways back, behaving differently.
//
// The list hands its current query string forward in `location.state` on the row click, and the
// detail page rebuilds its parent link with this. `location.state` is per-navigation and transient,
// so it is NOT a second source of truth for the narrowing — the URL is still the only thing that
// decides what the list shows. It is a hand-off, not a store, which is why this does not reopen D3.
//
// A missing/empty search (a deep link, a bookmark, a new tab) yields the bare list path, which is
// exactly today's behaviour — nothing to restore, nothing lost.
export const buildPipelineListUrl = (listPath, search) => {
	const query = String(search ?? '').replace(/^\?/, '');
	return query ? `${listPath}?${query}` : listPath;
};

export const isPipelineListNarrowed = (state = {}) => {
	const { searchTerm, appInstances, statuses } = { ...EMPTY_PIPELINE_LIST_VIEW_STATE, ...state };
	return Boolean(searchTerm) || (appInstances ?? []).length > 0 || (statuses ?? []).length > 0;
};

export default parsePipelineListViewState;
